import { useEffect, useRef } from 'react';
import { Outlet, useBlocker, useLocation } from 'react-router';
import { useMe } from '../api/hooks.js';
import { useUiStore } from '../store/ui.js';
import { confirmUnsaved, hasUnsaved } from '../store/unsaved.js';
import { useUsageHeartbeat } from '../lib/usageHeartbeat.js';
import { useEntitySwitchLanding } from '../lib/entitySwitch.js';
import { Sidebar } from './Sidebar.js';
import { Header, routeTitle } from './Header.js';
import { MobileNav } from './MobileNav.js';
import { BureauStrip } from './bureau/BureauStrip.js';
import { UnsavedChangesHost } from './UnsavedChangesModal.js';

// Top-level shell rendered around every protected route. The
// AuthGuard wrapping each protected route guarantees me.data is
// defined here. Layout follows the redesign mockup: a two-column
// .app grid (sidebar | main), with the sticky .topbar living inside
// main above the routed page.

export function AppShell() {
  const me = useMe();
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const drawerOpen = useUiStore((s) => s.drawerOpen);
  const closeDrawer = useUiStore((s) => s.closeDrawer);
  const switching = useUiStore((s) => s.entitySwitch);
  const landedSeq = useUiStore((s) => s.entityLandedSeq);
  const { pathname } = useLocation();
  const pageRef = useRef<HTMLDivElement>(null);
  // Connexions & usage: one heartbeat a minute while the tab is
  // visible, so "temps actif" measures use and not uptime.
  useUsageHeartbeat(Boolean(me.data));
  // Referme le changement de société quand ses données sont là.
  useEntitySwitchLanding(me.data?.user.role !== 'RECRUTEUR');

  // The .app grid's first column reads --sidebar-w. 248px expanded,
  // 64px collapsed (mockup geometry).
  useEffect(() => {
    document.documentElement.style.setProperty('--sidebar-w', collapsed ? '64px' : '248px');
  }, [collapsed]);

  // Atterrissage : rejoue la remontée en cascade du contenu. La classe
  // est retirée puis reposée après un reflow, sans démonter la page —
  // l'état local des écrans (date, vue, filtres) reste en place.
  useEffect(() => {
    if (!landedSeq) return undefined;
    const el = pageRef.current;
    if (!el) return undefined;
    el.classList.remove('is-landed');
    void el.offsetWidth;
    el.classList.add('is-landed');
    const t = window.setTimeout(() => el.classList.remove('is-landed'), 900);
    return () => window.clearTimeout(t);
  }, [landedSeq]);

  // ─── garde-fou des brouillons ───────────────────────────────
  // Changement de page (menu latéral, bouton Précédent) : même popup que
  // le changement de société. Seul le chemin compte — un ?entity= ou un
  // ?date= qui bouge n'est pas une sortie.
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      currentLocation.pathname !== nextLocation.pathname && hasUnsaved(),
  );
  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    let cancelled = false;
    void confirmUnsaved(routeTitle(blocker.location.pathname)).then((ok) => {
      if (cancelled) return;
      if (ok) blocker.proceed();
      else blocker.reset();
    });
    return () => {
      cancelled = true;
    };
  }, [blocker]);

  // Fermeture ou rechargement de l'onglet : alerte native du navigateur.
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!hasUnsaved()) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  if (!me.data) return null; // AuthGuard handles the loading/redirect state

  // Manager recruteur : ni barre latérale ni navigation mobile — une
  // seule page (Recrutement), sous une topbar réduite.
  if (me.data.user.role === 'RECRUTEUR') {
    return (
      <div className="app app-recruiter" data-testid="app-shell">
        <main>
          <Header user={me.data.user} minimal />
          <div className="page active" key={pathname} ref={pageRef} data-testid="page">
            <div className="page-body">
              <Outlet />
            </div>
          </div>
        </main>
        <UnsavedChangesHost />
      </div>
    );
  }

  return (
    <div className="app" data-testid="app-shell">
      <Sidebar permissions={me.data.permissions ?? []} modules={me.data.modules ?? null} />
      <main>
        <Header user={me.data.user} />
        <BureauStrip />
        {/* key=pathname remounts the page container on navigation so
            the CSS entrance animations (.page.active > … rise/scaleIn)
            re-trigger — the React equivalent of the mockup's reflow. */}
        <div
          className={switching ? 'page active is-switching' : 'page active'}
          key={pathname}
          ref={pageRef}
          data-testid="page"
        >
          <div className="page-body">
            <Outlet />
          </div>
        </div>
      </main>
      {drawerOpen && (
        <div className="drawer-backdrop" onClick={closeDrawer} data-testid="drawer-backdrop" />
      )}
      <MobileNav />
      <UnsavedChangesHost />
    </div>
  );
}
